import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { XCircle, ArrowLeft, RefreshCw, Mail, Phone, AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

const PaymentFailed = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const txnid = searchParams.get('txnid') || '';
  const reason = searchParams.get('reason') || 'Payment could not be completed';

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-100">
      {/* Error Hero */}
      <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)' }}>
        <div className="container mx-auto max-w-4xl px-6 py-20 relative z-10 text-center">
          {/* Error Icon */}
          <div className="inline-flex items-center justify-center w-24 h-24 bg-white rounded-full mb-8 shadow-2xl">
            <XCircle className="w-14 h-14 text-red-500" />
          </div>
          
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4">
            Payment Failed
          </h1>
          <p className="text-xl text-red-100 max-w-2xl mx-auto">
            Don't worry! Your payment was not processed and no amount was deducted.
          </p>
        </div>
      </div>

      {/* Error Details */}
      <div className="container mx-auto max-w-4xl px-6 -mt-10 relative z-20">
        <Card className="shadow-2xl border-0">
          <CardContent className="p-8">
            {/* Error Info */}
            <div className="bg-red-50 rounded-xl p-6 mb-8 border border-red-200">
              <div className="flex items-start gap-4">
                <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="text-lg font-bold text-gray-900 mb-2">What Happened?</h2>
                  <p className="text-gray-700" data-testid="error-reason">{reason}</p>
                  {txnid && (
                    <p className="text-sm text-gray-500 mt-2">
                      Transaction Reference: <span className="font-mono">{txnid}</span>
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Common Reasons */}
            <div className="mb-8">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Common Reasons for Payment Failure</h2>
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold text-gray-600">1</div>
                  <p className="text-sm text-gray-700">Insufficient balance in your account</p>
                </div>
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold text-gray-600">2</div>
                  <p className="text-sm text-gray-700">Card declined by issuing bank</p>
                </div>
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold text-gray-600">3</div>
                  <p className="text-sm text-gray-700">Network timeout or connectivity issues</p>
                </div>
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold text-gray-600">4</div>
                  <p className="text-sm text-gray-700">Incorrect card details or OTP</p>
                </div>
              </div>
            </div>

            {/* What to Do */}
            <div className="bg-blue-50 rounded-xl p-6 mb-8 border border-blue-200">
              <h2 className="text-lg font-bold text-gray-900 mb-3">What You Can Do</h2>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-blue-500">•</span>
                  Try again with the same or different payment method
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-500">•</span>
                  Check if your card has sufficient balance and is enabled for online transactions
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-500">•</span>
                  Try using a different browser or clear your browser cache
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-500">•</span>
                  Contact your bank if the issue persists
                </li>
              </ul>
            </div>

            {/* Contact Support */}
            <div className="border-t border-gray-200 pt-6 mb-8">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Need Assistance?</h2>
              <p className="text-sm text-gray-600 mb-4">
                If you're facing repeated issues, our team is here to help you complete your enrollment.
              </p>
              <div className="flex flex-wrap gap-4">
                <a href="mailto:shrey@workshoplibrary.com" className="flex items-center gap-2 text-blue-600 hover:text-blue-700">
                  <Mail className="w-5 h-5" />
                  shrey@workshoplibrary.com
                </a>
                <a href="https://wa.me/919891550666" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-green-600 hover:text-green-700">
                  <Phone className="w-5 h-5" />
                  WhatsApp Support
                </a>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => navigate('/payment')}
                className="flex-1 py-6 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                data-testid="try-again-button"
              >
                <RefreshCw className="w-5 h-5 mr-2" />
                Try Again
              </Button>
              <Button
                onClick={() => navigate('/csdgpm-offer')}
                variant="outline"
                className="flex-1 py-6"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Course
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <footer className="py-8 px-6 text-center mt-16">
        <p className="text-sm text-gray-600">© 2026 AI School for All. All rights reserved.</p>
        <p className="text-xs text-gray-400 mt-2">Powered by Workshop Library</p>
      </footer>
    </div>
  );
};

export default PaymentFailed;
